import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout } from '../auth.js';
import { db } from '../db/local.js';
import { unsentEstimateCount } from '../db/estimates.js';
import { pendingPhotoCount } from '../db/photos.js';
import { pendingCount } from '../sync/engine.js';

interface Risk {
  /** Änderungen in der Outbox — noch nicht beim Server. */
  pending: number;
  /** Fotos, die noch nicht hochgeladen sind. */
  photos: number;
  /** Foto-Schätzungen, deren Foto nur auf dem Gerät liegt (28.10.2026). */
  estimates: number;
}

/**
 * Abmelden — erst abgleichen, dann leeren (25.10.2026).
 *
 * Abmelden leert die lokale Datenbank (sonst landeten Daten im nächsten Konto),
 * die Outbox eingeschlossen. Ohne Netz waren so alle Eingaben seit dem letzten
 * Abgleich mit einem Tipp weg, ohne Nachfrage — stiller Datenverlust. Jetzt
 * gleicht der Knopf zuerst ab; bleibt danach etwas nur auf dem Gerät
 * (Änderungen oder Fotos), sagt er es und meldet erst nach Bestätigung ab.
 */
export function LogoutButton({
  onSync,
}: {
  /** Eine Abgleichrunde, wie beim Ziehen. Fehlt sie, wird nur gezählt. */
  onSync?: (() => Promise<unknown>) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const logout = useLogout();
  const [checking, setChecking] = useState(false);
  const [risk, setRisk] = useState<Risk | null>(null);

  async function start() {
    setChecking(true);
    try {
      // Scheitert der Abgleich (kein Netz), zählt danach die Outbox — das ist
      // die Antwort, nicht der Fehler.
      await onSync?.().catch(() => undefined);
      const [pending, photos, estimates] = await Promise.all([
        pendingCount(db),
        pendingPhotoCount(db),
        unsentEstimateCount(db),
      ]);
      if (pending === 0 && photos === 0 && estimates === 0) logout.mutate();
      else setRisk({ pending, photos, estimates });
    } finally {
      setChecking(false);
    }
  }

  const busy = checking || logout.isPending;

  if (risk) {
    return (
      <div role="alert" className="flex flex-col gap-2 rounded-xl border border-danger p-3">
        {risk.pending > 0 ? (
          <p className="text-sm text-danger">
            {t('settings.logoutPending', { count: risk.pending })}
          </p>
        ) : null}
        {risk.photos > 0 ? (
          <p className="text-sm text-danger">
            {t('settings.logoutPhotos', { count: risk.photos })}
          </p>
        ) : null}
        {risk.estimates > 0 ? (
          <p className="text-sm text-danger">
            {t('settings.logoutEstimates', { count: risk.estimates })}
          </p>
        ) : null}
        {/* Fotos gehen seit E21 immer hoch — auch für sie hilft erneutes Abgleichen. */}
        <button
          type="button"
          onClick={() => {
            setRisk(null);
            void start();
          }}
          disabled={busy}
          className="kw-btn kw-btn-small"
        >
          {t('settings.logoutRetry')}
        </button>
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={busy}
          className="kw-btn kw-btn-small bg-danger"
        >
          {t('settings.logoutAnyway')}
        </button>
        <button
          type="button"
          onClick={() => setRisk(null)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void start()}
      disabled={busy}
      className="kw-btn kw-btn-quiet kw-btn-small"
    >
      {checking ? t('settings.logoutChecking') : t('session.logout')}
    </button>
  );
}
